import { supabase } from '../lib/supabase';
import { clearEmpresaActiva, getEmpresaActivaId, setEmpresaActivaId } from '../lib/empresaActiva';

/**
 * Las empresas de la cuenta.
 *
 * Todo pasa por funciones de la base (`mis_empresas`, `crear_empresa`, …) y no por consultas
 * sueltas: la tabla `public.companies` no tiene permiso de escritura para el rol `authenticated`,
 * justo para que el tope de empresas por plan no se pueda esquivar desde el navegador. Ver la
 * migración 028.
 */

export interface Empresa {
  id: string;
  nombre: string;
  rnc: string | null;
  es_principal: boolean;
  activa: boolean;
  created_at: string;
  /** Tope del plan. `null` = sin tope. */
  max_empresas: number | null;
}

/** El error que lanza la base cuando el plan no da para otra empresa. */
export const PLAN_SIN_PLAZAS = 'PL002';

function esErrorDeTope(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === PLAN_SIN_PLAZAS;
}

export const empresasService = {
  async listar(): Promise<Empresa[]> {
    const { data, error } = await supabase.rpc('mis_empresas');
    if (error) throw error;
    return (data as Empresa[] | null) ?? [];
  },

  async crear(nombre: string, rnc?: string): Promise<Empresa> {
    const { data, error } = await supabase.rpc('crear_empresa', {
      p_nombre: nombre,
      p_rnc: rnc || null,
    });
    if (error) {
      if (esErrorDeTope(error)) {
        throw new Error(
          (error as { message?: string }).message
            || 'Tu plan no permite mas empresas. Mejoralo para gestionar mas de una.',
        );
      }
      throw error;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) throw new Error('No se pudo crear la empresa');
    return row as Empresa;
  },

  async renombrar(id: string, nombre: string, rnc?: string): Promise<void> {
    const { error } = await supabase.rpc('renombrar_empresa', {
      p_id: id,
      p_nombre: nombre,
      p_rnc: rnc || null,
    });
    if (error) throw error;
  },

  /**
   * Baja lógica: la empresa sale del selector y libera su plaza del plan, pero sus datos siguen
   * ahí. Reactivar vuelve a pasar por el tope, y puede fallar si entretanto se ocuparon las
   * plazas.
   */
  async archivar(id: string, activa = false): Promise<void> {
    const { error } = await supabase.rpc('archivar_empresa', { p_id: id, p_activa: activa });
    if (error) {
      if (esErrorDeTope(error)) {
        throw new Error(
          (error as { message?: string }).message
            || 'No hay plazas libres en tu plan para reactivar esta empresa.',
        );
      }
      throw error;
    }
    // Si se archiva la que se estaba mirando, hay que soltarla o la sesion se queda apuntando a
    // una empresa que ya no responde.
    if (!activa && getEmpresaActivaId() === id) clearEmpresaActiva();
  },

  /**
   * Cambia de empresa. Solo toca la preferencia local: el permiso lo sigue comprobando la base en
   * cada consulta. Se recarga para que todas las pantallas vuelvan a pedir sus datos con el
   * inquilino nuevo, en vez de quedarse con lo que ya tenian en memoria.
   */
  cambiarA(id: string) {
    setEmpresaActivaId(id);
    if (typeof window !== 'undefined') window.location.reload();
  },
};
